import { SuccessIcon } from '@/assets/icons';
import { FormError, FormikValues, TableTypes } from '@/constant';
import { useBeforeUnload, useEditableRowSubmit } from '@/hooks';
import { CommonActions } from '@/stores';
import React, {
	createContext,
	ForwardedRef,
	forwardRef,
	MouseEvent,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { InputRef } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import {
	EditActions,
	FAutoComplete,
	FDatePicker,
	FDateRangePicker,
	FSelectInput,
	FTextAreaInput,
	FTextInput,
	FTimePicker,
	FTimeRangePicker,
} from 'components';
import {
	cls,
	composeOtherValue,
	convertCamelToServerError,
	deepCompare,
	delay,
	formatDate,
	getHasServerErrorObject,
	hasErrorByKey,
	insertObjectIf,
	insertObjectIfV2,
	NOOP,
	parseOtherValue,
	showAlert,
} from 'utils';

import { Formik, FormikErrors, FormikProps, getIn } from 'formik';
import { omit, pickBy } from 'ramda';
import {
	isNilOrEmpty,
	isNonEmptyArray,
	isNotNilOrEmpty,
	isNotUndefined,
} from 'ramda-adjunct';

import {
	EditableCellProps,
	EditableCellType,
	EditableRowProps,
	ResizableCellProps,
} from '../type';

const EditableContext = createContext<
	| (FormikProps<FormikValues> & {
			onSubmit: (
				values: FormikValues,
				callback?: ({ error, data }: any) => void,
				removeKeys?: string[]
			) => void;
			editingCells: Record<string, boolean>;
			toggleEditCell: (dataIndex: string, value?: boolean) => void;
			closeAllEditCells: () => void;
	  })
	| null
>(null);

type FormikRowProps = {
	formikProps: FormikProps<FormikValues>;
	onSubmit: (
		values: FormikValues,
		callback?: ({ error, data }: any) => void,
		removeKeys?: string[]
	) => void;
	props: any;
	rowRef: RefObject<EditableRowRef> | null;
};

const FormikRow = ({
	formikProps,
	onSubmit,
	props,
	rowRef,
}: FormikRowProps) => {
	const dispatch = useDispatch();

	const [editingCells, setEditingCells] = useState<FormikValues>({});

	const isDirty = useMemo(() => {
		return Object.keys(formikProps.initialValues || {})?.some(
			(key) =>
				!deepCompare(formikProps.values[key], formikProps.initialValues[key])
		);
	}, [formikProps.values, formikProps.initialValues]);

	useBeforeUnload(isDirty);

	useEffect(() => {
		dispatch(CommonActions.setHasFormChange(isDirty));
	}, [isDirty]);

	useEffect(() => {
		// eslint-disable-next-line react/prop-types
		React.Children.map(props?.children, (child) => {
			setEditingCells((prev: any) => ({
				...prev,
				[child?.props?.dataIndex]: false,
			}));
		});
	}, []);

	useImperativeHandle(rowRef, () => ({
		focusFieldError: async () => {
			const fieldsError = await formikProps.validateForm(formikProps?.values);

			const firstErrorField = Object.keys(fieldsError)[0] as string;

			if (editingCells[firstErrorField]) {
				return;
			}

			toggleEditCell(firstErrorField);
		},
	}));

	const toggleEditCell = useCallback((dataIndex: string, value?: boolean) => {
		setEditingCells((prev: any) => ({
			...prev,
			[dataIndex]: isNotUndefined(value) ? value : !prev?.[dataIndex],
		}));
	}, []);

	const closeAllEditCells = useCallback(() => {
		setEditingCells((prev: {}) => {
			const updated: FormikValues = {};
			Object.keys(prev).forEach((key) => {
				updated[key] = false; // Set each cell to non-editing (false)
			});
			return updated;
		});
	}, []);

	return (
		<EditableContext.Provider
			value={{
				...formikProps,
				onSubmit,
				editingCells,
				toggleEditCell,
				closeAllEditCells,
			}}
		>
			<tr {...props} />
		</EditableContext.Provider>
	);
};

export type EditableRowRef = {
	focusFieldError: () => void;
};

export const EditableRowComponent = <T extends TableTypes>(
	{
		validationSchema,
		tableType,
		needToReloadListAfterClosePopup,
		...props
	}: EditableRowProps<T>,
	ref: ForwardedRef<RefObject<EditableRowRef>>
) => {
	const { onSubmit } = useEditableRowSubmit({
		tableType,
		needToReloadListAfterClosePopup,
	});

	return (
		<Formik
			initialValues={props?.children?.[0]?.props?.record as any}
			enableReinitialize
			onSubmit={NOOP}
			validationSchema={validationSchema}
			validateOnMount
			validateOnBlur
		>
			{(formikProps: FormikProps<FormikValues>) => (
				<FormikRow
					formikProps={formikProps}
					onSubmit={onSubmit}
					props={props}
					rowRef={ref as any}
				/>
			)}
		</Formik>
	);
};

export const EditableRow = forwardRef(EditableRowComponent);

EditableRow.displayName = 'EditableRow';

export const EditableCell = ({
	title,
	editable,
	children,
	dataIndex,
	keyUpdate,
	record,
	handleSave,
	handleError,
	callbackSuccess,
	cellType,
	dropdownProps,
	inputProps,
	textAreaProps,
	dateProps,
	autoCompleteProps,
	renderCell,
	objectName,
	fieldsToClose,
	removeKeys,
	...restProps
}: EditableCellProps) => {
	const { t } = useTranslation(['message', 'validation']);

	const [showSuccess, setShowSuccess] = useState(false);

	const inputRef = useRef<InputRef>(null);
	const form = useContext(EditableContext)!;

	const editing = form?.editingCells?.[dataIndex];

	useEffect(() => {
		if (editing) {
			inputRef.current?.focus();
		}

		//TODO: GlobalDispatch(GeneralActions.setDisabledNav(editing));
	}, [editing]);

	const toggleEdit = useCallback(
		(
			e?: React.MouseEvent<HTMLDivElement>,
			getAdditionalValues?: (
				value: string,
				prevValues: FormikProps<FormikValues>['values']
			) => FormikProps<FormikValues>['values'] | void
		) => {
			e?.stopPropagation?.();
			form.toggleEditCell(dataIndex);

			if (editing) {
				const serverErrorKey = convertCamelToServerError(
					String(keyUpdate || dataIndex)
				);
				form.setValues((prevValues) => ({
					...prevValues,
					[dataIndex]: record?.[dataIndex],
					...insertObjectIf(Object.hasOwn(prevValues, serverErrorKey), {
						[serverErrorKey]: false,
					}),
					...(getAdditionalValues
						? getAdditionalValues?.(record?.[dataIndex], prevValues) ?? {}
						: {}),
					...(dropdownProps?.getAdditionalValues
						? dropdownProps?.getAdditionalValues?.(
								record?.[dataIndex],
								prevValues
							) ?? {}
						: {}),
				}));

				if (isNonEmptyArray(fieldsToClose)) {
					fieldsToClose?.forEach((field) => {
						form.toggleEditCell(field, false);
					});
				}
			}
		},
		[
			form,
			keyUpdate,
			dataIndex,
			record,
			editing,
			dropdownProps?.getAdditionalValues,
			fieldsToClose,
		]
	);

	const callbackOnSubmit = useCallback(
		async ({ error, data }: any) => {
			if (error) {
				console.error('error', error);

				const hasServerErrorObject = getHasServerErrorObject(
					error,
					objectName ? `${objectName}_` : ''
				);

				form.setValues((prevValues) => ({
					...prevValues,
					...hasServerErrorObject,
					...(dropdownProps?.getAdditionalValuesOnError
						? dropdownProps?.getAdditionalValuesOnError?.(
								form.values?.[dataIndex],
								prevValues,
								form?.initialValues
							) ?? {}
						: {}),
				}));
				return;
			}

			toggleEdit();
			form?.closeAllEditCells();

			setShowSuccess(true);
			await delay(1000);
			callbackSuccess?.(data);
			await delay(2000);
			setShowSuccess(false);
		},
		[
			toggleEdit,
			objectName,
			form,
			dropdownProps?.getAdditionalValuesOnError,
			record,
			dataIndex,
			callbackSuccess,
		]
	);

	const handleValidateFormErrors = useCallback(
		(error: FormikErrors<FormikValues>) => {
			form.submitForm();
			switch (true) {
				case hasErrorByKey(error, FormError.FORMAT_COMMON_INVALID):
					showAlert(t('message:COMMON_INVALID_REQUEST'), () => {
						inputRef.current?.focus();
					});
					break;
				case hasErrorByKey(error, FormError.FORMAT):
					showAlert(t('validation:ALERT_13'), () => {
						inputRef.current?.focus();
					});
					break;
				case hasErrorByKey(error, FormError.IPV4):
					showAlert('올바른 IPv4형식이 아닙니다.', () => {
						inputRef.current?.focus();
					});
					break;
				default:
					showAlert(t('validation:ALERT_12'), () => {
						inputRef.current?.focus();
					});
					break;
			}

			handleError?.(record);
		},
		[form, t, inputRef, handleError, record]
	);

	const save = useCallback(async () => {
		const error = await form.validateForm(form.values);

		if (isNotNilOrEmpty(error)) {
			handleValidateFormErrors(error);
			return;
		}

		if (record?.[dataIndex] === form.values?.[dataIndex]) {
			toggleEdit();

			return;
		}

		const formValuesChanges = pickBy(
			(value, key) =>
				key in form.initialValues &&
				!deepCompare(form.initialValues[key], value),
			form.values
		) as FormikValues;

		let data: FormikValues = {
			id: record?.id,
			...formValuesChanges,
		};

		if (keyUpdate) {
			data = omit([dataIndex], {
				...data,
				[keyUpdate]: form.values?.[dataIndex],
			});
		}

		form.onSubmit(data, callbackOnSubmit, removeKeys);
		handleSave?.(data, toggleEdit);
	}, [
		form,
		dataIndex,
		keyUpdate,
		record,
		cellType,
		removeKeys,
		toggleEdit,
		handleSave,
		handleError,
		callbackOnSubmit,
		handleValidateFormErrors,
	]);

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				save();
			}
		},
		[save]
	);

	const onChangeSelectInput = useCallback(
		async (value: string, option?: DefaultOptionType) => {
			// empty and null are the same, so not need to toggleEdit
			if (
				form.values?.[dataIndex] === value ||
				(isNilOrEmpty(value) && isNilOrEmpty(form.values?.[dataIndex]))
			) {
				// toggleEdit();

				return;
			}

			form.setFieldValue(dataIndex, value);

			const error = await form.validateForm({
				...form.values,
				[dataIndex]: value,
			});

			if (isNotNilOrEmpty(error)) {
				handleValidateFormErrors(error);
				return;
			}

			const formValuesChanges = pickBy(
				(value, key) =>
					key in form.initialValues &&
					!deepCompare(form.initialValues[key], value),
				{
					...form.values,
					[dataIndex]: value,
				}
			) as FormikValues;

			const additionalValues = dropdownProps?.getAdditionalValues
				? dropdownProps?.getAdditionalValues?.(value, form?.values) ?? {}
				: {};

			let data: FormikValues = {
				id: record?.id,
				...formValuesChanges,
				...additionalValues,
			};

			if (keyUpdate) {
				data = omit([dataIndex], {
					...data,
					[keyUpdate]: value,
				});
			}

			if (!dropdownProps?.ignoreSubmitKeys?.includes(dataIndex)) {
				form.onSubmit(data, callbackOnSubmit, removeKeys);
				return;
			}

			handleSave?.(
				{ ...data, option },
				() => {
					callbackOnSubmit?.({
						error: null,
						data,
					});
				},
				(newData?: FormikValues) => {
					form.onSubmit(
						{
							...data,
							...newData,
						},
						callbackOnSubmit,
						removeKeys
					);
				},
				async (closeEdit?: boolean) => {
					await form.setValues((prevValues) => ({
						...prevValues,
						[dataIndex]: form.initialValues?.[dataIndex],
						...insertObjectIf(inputProps?.name, {
							[inputProps?.name as string]:
								form.initialValues?.[inputProps?.name as string],
						}),
					}));

					if (closeEdit) {
						toggleEdit();
					}
				}
			);
		},
		[
			form,
			dataIndex,
			keyUpdate,
			record,
			handleSave,
			handleError,
			toggleEdit,
			callbackOnSubmit,
			dropdownProps?.ignoreSubmitKeys,
			dropdownProps?.getAdditionalValues,
			handleValidateFormErrors,
			removeKeys,
			inputProps?.name,
		]
	);

	const onSaveDate = useCallback(async () => {
		const error = await form.validateForm(form.values);
		if (isNotNilOrEmpty(error)) {
			handleValidateFormErrors(error);
			return;
		}

		const outputFormat = dateProps?.format || 'YYYY-MM-DD';

		const currentDate = form.initialValues?.[dataIndex]
			? formatDate(form.initialValues?.[dataIndex], { outputFormat })
			: null;

		const newDate = form.values?.[dataIndex]
			? formatDate(form.values?.[dataIndex], { outputFormat })
			: null;

		if (currentDate === newDate) {
			toggleEdit();

			return;
		}

		const formValuesChanges = pickBy(
			(value, key) =>
				key in form.initialValues &&
				!deepCompare(form.initialValues[key], value),
			{
				...form.values,
				[dataIndex]: newDate,
			}
		) as FormikValues;

		let data: FormikValues = {
			id: record?.id,
			...formValuesChanges,
		};

		if (keyUpdate) {
			data = omit([dataIndex], {
				...data,
				[keyUpdate]: form.values?.[dataIndex],
			});
		}

		if (!dateProps?.ignoreSubmitKeys?.includes(dataIndex)) {
			form.onSubmit(data, callbackOnSubmit, removeKeys);
			return;
		}

		handleSave?.(
			data,
			() => {
				callbackOnSubmit?.({
					error: null,
					data,
				});
			},
			(newData?: FormikValues) => {
				form.onSubmit(
					{
						...data,
						...newData,
					},
					callbackOnSubmit,
					removeKeys
				);
			},
			async (closeEdit?: boolean) => {
				await form.setValues((prevValues) => ({
					...prevValues,
					...form.initialValues,
				}));

				if (closeEdit) {
					toggleEdit();
				}
			}
		);
	}, [
		form,
		dataIndex,
		keyUpdate,
		record,
		handleSave,
		handleError,
		toggleEdit,
		callbackOnSubmit,
		handleValidateFormErrors,
		dateProps,
		removeKeys,
	]);

	const onSaveDateRange = useCallback(async () => {
		const error = await form.validateForm(form.values);

		if (isNotNilOrEmpty(error)) {
			handleValidateFormErrors(error);
			return;
		}

		const { startDateKey = 'startDate', endDateKey = 'endDate' } =
			dateProps || {};

		const currentRangeDateValues = {
			[startDateKey]: form.initialValues?.[startDateKey],
			[endDateKey]: form.initialValues?.[endDateKey],
		};
		const newRangeDateValues = {
			[startDateKey]: form.values?.[startDateKey],
			[endDateKey]: form.values?.[endDateKey],
		};

		if (deepCompare(currentRangeDateValues, newRangeDateValues)) {
			toggleEdit();

			return;
		}

		const formValuesChanges = pickBy(
			(value, key) =>
				key in form.initialValues &&
				!deepCompare(form.initialValues[key], value),
			{
				...form.values,
				...newRangeDateValues,
			}
		) as FormikValues;

		const data = {
			id: record?.id,
			...omit([dataIndex], formValuesChanges),
		};

		form.onSubmit(data, callbackOnSubmit);
		handleSave?.(data, toggleEdit);
	}, [
		form,
		dataIndex,
		keyUpdate,
		record,
		handleSave,
		handleError,
		toggleEdit,
		callbackOnSubmit,
		handleValidateFormErrors,
	]);

	const onSaveTime = useCallback(async () => {
		const error = await form.validateForm(form.values);
		if (isNotNilOrEmpty(error)) {
			handleValidateFormErrors(error);
			return;
		}

		const outputFormat = dateProps?.format || 'HH:mm';

		const currentTime = form.initialValues?.[dataIndex]
			? formatDate(form.initialValues?.[dataIndex], { outputFormat })
			: null;

		const newTime = form.values?.[dataIndex]
			? formatDate(form.values?.[dataIndex], { outputFormat })
			: null;

		if (currentTime === newTime) {
			toggleEdit();

			return;
		}

		const formValuesChanges = pickBy(
			(value, key) =>
				key in form.initialValues &&
				!deepCompare(form.initialValues[key], value),
			{
				...form.values,
				[dataIndex]: newTime,
			}
		) as FormikValues;

		let data: FormikValues = {
			id: record?.id,
			...formValuesChanges,
		};

		if (keyUpdate) {
			data = omit([dataIndex], {
				...data,
				[keyUpdate]: form.values?.[dataIndex],
			});
		}

		form.onSubmit(data, callbackOnSubmit);
		handleSave?.(data, toggleEdit);
	}, [
		form,
		dataIndex,
		keyUpdate,
		record,
		handleSave,
		handleError,
		toggleEdit,
		callbackOnSubmit,
		handleValidateFormErrors,
	]);

	const onSaveTimeRange = useCallback(async () => {
		const { startTimeKey = 'startTime', endTimeKey = 'endTime' } =
			dateProps || {};

		const error = await form.validateForm(form.values);

		if (isNotNilOrEmpty(error)) {
			handleValidateFormErrors(error);
			return;
		}

		const currentRangeDateValues = {
			[startTimeKey]: form.initialValues?.[startTimeKey],
			[endTimeKey]: form.initialValues?.[endTimeKey],
		};

		const newRangeDateValues = {
			[startTimeKey]: form.values?.[startTimeKey],
			[endTimeKey]: form.values?.[endTimeKey],
		};

		if (deepCompare(currentRangeDateValues, newRangeDateValues)) {
			toggleEdit();

			return;
		}

		const formValuesChanges = pickBy(
			(value, key) =>
				key in form.initialValues &&
				!deepCompare(form.initialValues[key], value),
			{
				...form.values,
				...newRangeDateValues,
			}
		) as FormikValues;

		const data = {
			id: record?.id,
			...formValuesChanges,
		};

		form.onSubmit(data, callbackOnSubmit);
		handleSave?.(data, toggleEdit);
	}, [
		form,
		dataIndex,
		keyUpdate,
		record,
		handleSave,
		handleError,
		toggleEdit,
		callbackOnSubmit,
		handleValidateFormErrors,
	]);

	const onChangeSelectDropdownInput = useCallback(
		async (value: string, option?: DefaultOptionType) => {
			const { hasOther } = parseOtherValue(value, dropdownProps?.otherToken);
			if (!hasOther) {
				form.setFieldValue(inputProps?.name || '', '');

				await onChangeSelectInput(value, option);
				return;
			}
		},
		[dropdownProps?.otherToken, form, inputProps?.name, onChangeSelectInput]
	);

	const onSaveDropdownInput = useCallback(async () => {
		const error = await form.validateForm(form.values);
		if (isNotNilOrEmpty(error)) {
			handleValidateFormErrors(error);
			return;
		}

		const selectedValue = form.values?.[dataIndex];

		const isOtherSelected = parseOtherValue(
			selectedValue,
			dropdownProps?.otherToken
		).hasOther;

		const finalValue = isOtherSelected
			? composeOtherValue(
					form.values?.[inputProps?.name as string],
					dropdownProps?.otherToken
				)
			: selectedValue;

		// Skip if no changes
		if (form.initialValues?.[dataIndex] === finalValue) {
			toggleEdit();
			return;
		}

		const formValuesChanges = pickBy(
			(value, key) =>
				key in form.initialValues &&
				!deepCompare(form.initialValues[key], value),
			{
				...form.values,
				[dataIndex]: finalValue,
			}
		) as FormikValues;

		let data: FormikValues = {
			id: record?.id,
			...formValuesChanges,
		};

		if (keyUpdate) {
			data = omit([dataIndex], {
				...data,
				[keyUpdate]: finalValue,
			});
		}

		if (!inputProps?.ignoreSubmitKeys?.includes(dataIndex)) {
			form.onSubmit(data, callbackOnSubmit, removeKeys);
			return;
		}

		handleSave?.(
			{ ...data },
			() => {
				callbackOnSubmit?.({
					error: null,
					data,
				});
			},
			(newData?: FormikValues) => {
				form.onSubmit(
					{
						...data,
						...newData,
					},
					callbackOnSubmit,
					removeKeys
				);
			},
			async (closeEdit?: boolean) => {
				await form.setValues((prevValues) => ({
					...prevValues,
					[dataIndex]: form.initialValues?.[dataIndex],
					...insertObjectIf(inputProps?.name, {
						[inputProps?.name as string]:
							form.initialValues?.[inputProps?.name as string],
					}),
				}));

				if (closeEdit) {
					toggleEdit();
				}
			}
		);
	}, [
		form,
		dataIndex,
		keyUpdate,
		record,
		dropdownProps?.otherToken,
		inputProps?.name,
		handleSave,
		handleError,
		toggleEdit,
		callbackOnSubmit,
		handleValidateFormErrors,
		removeKeys,
		inputProps?.ignoreSubmitKeys,
	]);

	if (renderCell) {
		return (
			<td {...restProps}>
				{renderCell({
					form,
					record,
					toggleEdit,
					editing,
				})}
			</td>
		);
	}

	const onCancelDateRange = useCallback(() => {
		const { startDateKey = 'startDate', endDateKey = 'endDate' } =
			dateProps || {};
		toggleEdit(undefined, () => ({
			[startDateKey]: form.initialValues?.[startDateKey],
			[endDateKey]: form.initialValues?.[endDateKey],
		}));
	}, [form, toggleEdit]);

	const onCancelTimeRange = useCallback(() => {
		const { startTimeKey = 'startTime', endTimeKey = 'endTime' } =
			dateProps || {};
		toggleEdit(undefined, () => ({
			[startTimeKey]: form.initialValues?.[startTimeKey],
			[endTimeKey]: form.initialValues?.[endTimeKey],
		}));
	}, [form, toggleEdit]);

	const onCancelDropdownInput = useCallback(() => {
		if (!inputProps?.name) {
			toggleEdit();
			return;
		}
		toggleEdit(undefined, () => ({
			[dataIndex]: form.initialValues?.[dataIndex],
			[inputProps.name as string]: form.initialValues?.[inputProps?.name || ''],
		}));
	}, [inputProps?.name, toggleEdit, form]);

	let childNode = children;

	if (editable || editing) {
		switch (cellType) {
			case EditableCellType.AUTOCOMPLETE:
				childNode = (
					<div className="row-between gap-x-2">
						<FAutoComplete
							name={dataIndex}
							inputClassName="w-full min-w-28"
							onKeyDown={onKeyDown}
							autoFocus
							value={form.values?.[dataIndex]}
							{...autoCompleteProps}
						/>
						<EditActions onCancel={toggleEdit} onSave={save} />
					</div>
				);
				break;
			case EditableCellType.DROPDOWN: {
				const { optionsByRecord, disableByRecord, ..._dropdownProps } =
					dropdownProps || {};
				let options = optionsByRecord
					? optionsByRecord(record)
					: dropdownProps?.options;
				let disabled = disableByRecord
					? disableByRecord(record)
					: dropdownProps?.disabled;
				const serverErrorKey = convertCamelToServerError(
					String(keyUpdate || dataIndex)
				);

				childNode = (
					<div className="row-between gap-x-2">
						<FSelectInput
							name={dataIndex}
							className="w-full min-w-28 text-left [&>div.ant-select-selector]:px-[11px]"
							autoFocus
							value={form.values?.[dataIndex] ?? ''}
							hasServerError={getIn(form?.values, serverErrorKey as string)}
							onSelect={onChangeSelectInput}
							options={options}
							disabled={disabled}
							{..._dropdownProps}
						/>
						<EditActions onCancel={toggleEdit} hiddenSave />
					</div>
				);
				break;
			}
			case EditableCellType.TEXT_INPUT:
			case EditableCellType.CURRENCY: {
				const { disableByRecord, ..._inputProps } = inputProps || {};
				const serverErrorKey = convertCamelToServerError(
					String(keyUpdate || dataIndex)
				);
				let disabled = disableByRecord
					? disableByRecord(record)
					: inputProps?.disabled;
				childNode = (
					<div className="row-between gap-x-2">
						<FTextInput
							currency={cellType === EditableCellType.CURRENCY}
							inputRef={inputRef}
							name={dataIndex}
							onKeyDown={onKeyDown}
							inputClassName="min-w-28"
							hasServerError={getIn(form?.values, serverErrorKey as string)}
							{..._inputProps}
							{...insertObjectIfV2(_inputProps?.isUploadFileName, {
								fileExtension: record?.[dataIndex]?.split?.('.')?.pop(),
							})}
							disabled={disabled}
						/>
						<EditActions onCancel={toggleEdit} onSave={save} />
					</div>
				);
				break;
			}

			case EditableCellType.TEXTAREA:
				childNode = (
					<div className="row-between gap-x-2">
						<FTextAreaInput
							name={dataIndex}
							className="min-w-44"
							{...textAreaProps}
						/>
						<EditActions onCancel={toggleEdit} onSave={save} />
					</div>
				);
				break;

			case EditableCellType.DATE: {
				const serverErrorKey = convertCamelToServerError(
					String(keyUpdate || dataIndex)
				);
				childNode = (
					<div className="row-between gap-x-2">
						<FDatePicker
							name={dataIndex}
							inputClassName="min-w-28"
							hasServerError={getIn(form?.values, serverErrorKey as string)}
							onSave={onSaveDate}
							{...dateProps}
						/>
						<EditActions onCancel={toggleEdit} onSave={onSaveDate} />
					</div>
				);
				break;
			}
			case EditableCellType.DATERANGE:
				childNode = (
					<div className="row-between gap-x-2">
						<FDateRangePicker
							inputClassName="min-w-36"
							values={form?.values}
							{...dateProps}
						/>
						<EditActions
							onCancel={onCancelDateRange}
							onSave={onSaveDateRange}
						/>
					</div>
				);
				break;

			case EditableCellType.TIME: {
				const serverErrorKey = convertCamelToServerError(
					String(keyUpdate || dataIndex)
				);
				childNode = (
					<div className="row-between gap-x-2">
						<FTimePicker
							name={dataIndex}
							inputClassName="min-w-28"
							hasServerError={getIn(form?.values, serverErrorKey as string)}
							onSave={onSaveTime}
							showTime
							{...dateProps}
						/>
						<EditActions onCancel={toggleEdit} onSave={onSaveTime} />
					</div>
				);
				break;
			}
			case EditableCellType.TIMERANGE:
				childNode = (
					<div className="row-between gap-x-2">
						<FTimeRangePicker
							inputClassName="min-w-28"
							values={form?.values}
							allowClear={false}
							{...dateProps}
						/>
						<EditActions
							onCancel={onCancelTimeRange}
							onSave={onSaveTimeRange}
						/>
					</div>
				);
				break;

			case EditableCellType.DROPDOWN_INPUT: {
				const { optionsByRecord, disableByRecord, ..._dropdownProps } =
					dropdownProps || {};
				const options = optionsByRecord
					? optionsByRecord(record)
					: dropdownProps?.options;
				const disabled = disableByRecord
					? disableByRecord(record)
					: dropdownProps?.disabled;
				const inputDisabled = inputProps?.disableByRecord
					? inputProps.disableByRecord(record)
					: inputProps?.disabled;

				const { hasOther } = parseOtherValue(
					form.values?.[dataIndex],
					dropdownProps?.otherToken
				);

				const selectedValue = hasOther
					? dropdownProps?.otherToken
					: form.values?.[dataIndex] || '';

				childNode = (
					<div className="row-between gap-x-2 w-full">
						<FSelectInput
							name={dataIndex}
							className="w-[200px] min-w-28 text-left [&>div.ant-select-selector]:px-[11px]"
							autoFocus
							value={selectedValue}
							onSelect={onChangeSelectDropdownInput}
							options={options}
							disabled={disabled}
							{..._dropdownProps}
						/>

						{hasOther && inputProps?.name && (
							<FTextInput
								name={inputProps?.name}
								inputClassName="min-w-28 flex-1"
								disabled={inputDisabled}
								{...inputProps}
							/>
						)}

						<EditActions
							onCancel={onCancelDropdownInput}
							onSave={onSaveDropdownInput}
							hiddenSave={!hasOther}
						/>
					</div>
				);
				break;
			}
			default:
				break;
		}

		childNode = editing ? (
			<div className="editable-cell" onClick={(e) => e?.stopPropagation?.()}>
				{childNode}
			</div>
		) : (
			<div
				className={cls(
					'editable-cell cursor-text row-between gap-x-2',
					!children?.[1] && 'absolute top-0 left-0 right-0 bottom-0'
				)}
				onClick={(e: React.MouseEvent<HTMLDivElement>) => toggleEdit(e)}
			>
				<div className="h-full w-full">{children}</div>
				{showSuccess && (
					<span className="ml-auto">
						<SuccessIcon />
					</span>
				)}
			</div>
		);
	}

	return <td {...restProps}>{childNode}</td>;
};

export const ResizableCell = (props: ResizableCellProps) => {
	const { onResize, children, className, ...restProps } = props;

	const resizableRef = useRef<HTMLTableCellElement | null>(null);

	const onMouseMove = useCallback(
		(e: globalThis.MouseEvent) => {
			e.stopPropagation();
			if (!resizableRef.current) return;
			document.body.style.cursor = 'w-resize';

			const rect = resizableRef.current.getBoundingClientRect();
			const newWidth = e.clientX - rect.left;

			if (onResize) {
				onResize(newWidth);
			}
		},
		[onResize]
	);

	const onResizableMouseDown = useCallback(
		(e: MouseEvent<HTMLDivElement>) => {
			e.preventDefault();

			const onMouseUp = () => {
				document.body.style.cursor = 'auto';
				document.removeEventListener('mousemove', onMouseMove);
				document.removeEventListener('mouseup', onMouseUp);
			};

			document.addEventListener('mousemove', onMouseMove);
			document.addEventListener('mouseup', onMouseUp);
		},
		[onMouseMove]
	);

	return (
		<th
			ref={resizableRef}
			{...restProps}
			className={`resizable relative  ${className || ''}`}
		>
			{children}
			<div
				onMouseDown={onResizableMouseDown}
				onClick={(e) => e.stopPropagation?.()}
				className="absolute -right-2.5 top-0 h-full w-5 cursor-w-resize z-20"
			></div>
		</th>
	);
};
